import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ESTIMATE_RULES,
  applyMetadata,
  applyTake,
  barAndBeat,
  canRemoveOccurrence,
  checkBars,
  completeBeatMap,
  displayChord,
  estimateBeatMap,
  hasErrors,
  hasTapped,
  meterOf,
  moveOccurrence,
  removeOccurrence,
  repeatOccurrence,
  requantize,
  sameContent,
  setEventBeats,
  timingOf,
  validateContent,
  type EstimateRule,
  type Grid,
  type RevisionContent,
  type SongDocument,
} from '../../domain'
import { saveDetail, takeResumeDetail } from '../../platform/session'
import { getDb } from '../../storage/db'
import { getPractice, getSongDocument, saveRevision } from '../../storage/repository'
import { MAX_BPM, MIN_BPM, tapTempo } from '../../transport/tap'
import common from '../common.module.css'
import { useLive } from '../hooks/useLive'
import { sectionJumpLabel } from '../play/sectionLabel'
import { useTransport } from '../play/useTransport'
import { usePreferences } from '../prefs'
import { paths } from '../router'
import { t } from '../strings'
import styles from './Timing.module.css'

const METERS = ['4/4', '3/4', '2/4', '6/8', '12/8']
const DEFAULT_BPM = 80
/** Dos marcas a menos de esto son un toque doble, no dos cambios. */
const MIN_MARK_GAP = 0.15
/** Durante la grabación la canción no se acaba: la toma puede durar más que la estimación. */
const RECORDING_TOTAL = 100_000

const clampBpm = (bpm: number) => Math.min(MAX_BPM, Math.max(MIN_BPM, Math.round(bpm)))

export function Timing({ arrangementId }: { arrangementId: string }) {
  const { data: doc, loading } = useLive(
    () => getSongDocument(getDb(), arrangementId),
    [arrangementId],
  )
  const { data: practice, loading: practiceLoading } = useLive(
    () => getPractice(getDb(), arrangementId),
    [arrangementId],
  )
  // El aviso vive fuera de la vista: al guardar cambia la revisión y la vista se monta de nuevo.
  const [notice, setNotice] = useState<string | null>(null)
  useEffect(() => {
    if (notice === null) return
    const timer = setTimeout(() => setNotice(null), 5000)
    return () => clearTimeout(timer)
  }, [notice])
  if (loading || practiceLoading) return <p className={common.muted}>{t.play.loading}</p>
  if (!doc) {
    return (
      <div className={common.page}>
        <p className={common.noticeError}>{t.play.notFound}</p>
        <a href={`#${paths.repertoire}`}>{t.play.back}</a>
      </div>
    )
  }
  const bpm = practice?.practiceBpm ?? doc.revision.content.tempo.reference?.bpm ?? DEFAULT_BPM
  return (
    <TimingView
      key={doc.revision.id}
      doc={doc}
      initialBpm={bpm}
      notice={notice}
      onNotice={setNotice}
    />
  )
}

interface Recording {
  /** Evento del mapa desde el que se graba. */
  from: number
  withClick: boolean
  /** Pulso de la canción donde empieza la toma (con clic). */
  startBeat: number
  /** Instante del primer toque (sin clic), en milisegundos del reloj de eventos. */
  origin: number | null
  marks: number[]
}

interface SavedDraft {
  baseRevisionId: string
  content: RevisionContent
}

/** La regla con la que se estimó el mapa, si sigue siendo una estimación sin tocar. */
function ruleOf(content: RevisionContent): EstimateRule | null {
  const origin = content.beatMap.origin ?? ''
  const rule = origin.startsWith('rule:') ? origin.slice(5) : ''
  return (ESTIMATE_RULES as readonly string[]).includes(rule) && !hasTapped(content.beatMap)
    ? (rule as EstimateRule)
    : null
}

interface TimingViewProps {
  doc: SongDocument
  initialBpm: number
  notice: string | null
  onNotice: (notice: string | null) => void
}

/** Pulsos con coma decimal y como mucho dos decimales: «2», «2,5», «2,01». */
const formatBeats = (beats: number) => String(Math.round(beats * 100) / 100).replace('.', ',')

function TimingView({ doc, initialBpm, notice, onNotice: setNotice }: TimingViewProps) {
  const { notation, countInBars, metronome } = usePreferences()
  const arrangementId = doc.arrangement.id
  const route = paths.timing(arrangementId)
  const base = doc.revision.content

  // Punto de partida: el mapa de la ficha, completado; sin mapa, la estimación.
  const [startingPoint] = useState<RevisionContent>(() => ({
    ...base,
    beatMap: completeBeatMap(base).beatMap,
  }))
  const [draft, setDraft] = useState<RevisionContent>(() => {
    const saved = takeResumeDetail(route)?.draft as SavedDraft | undefined
    return saved?.baseRevisionId === doc.revision.id ? saved.content : startingPoint
  })
  const [grid, setGrid] = useState<Grid>(1)
  const [withClick, setWithClick] = useState(true)
  const [recording, setRecordingState] = useState<Recording | null>(null)
  // Las marcas llegan seguidas: se leen de una referencia para no perder ninguna entre repintados.
  const recordingRef = useRef<Recording | null>(null)
  const setRecording = useCallback((next: Recording | null) => {
    recordingRef.current = next
    setRecordingState(next)
  }, [])
  const [saving, setSaving] = useState(false)
  const taps = useRef<number[]>([])

  const dirty = !sameContent(draft, startingPoint)
  useEffect(() => {
    saveDetail(dirty ? { draft: { baseRevisionId: doc.revision.id, content: draft } } : {})
  }, [dirty, draft, doc.revision.id])

  const meter = useMemo(() => meterOf(draft), [draft])
  const timing = useMemo(() => timingOf(draft.beatMap), [draft.beatMap])
  const bars = useMemo(() => checkBars(draft, draft.beatMap), [draft])
  const symbols = useMemo(
    () =>
      new Map(
        draft.sections.flatMap((section) =>
          section.lines.flatMap((line) => line.chords.map((anchor) => [anchor.id, anchor.chord])),
        ),
      ),
    [draft.sections],
  )
  const show = useCallback(
    (symbol: string) => displayChord(symbol, notation, { unicode: true }),
    [notation],
  )
  const symbolAt = (index: number) => {
    const event = timing.events[index]
    if (!event) return '–'
    return event.chord ? show(symbols.get(event.chord) ?? '?') : '·'
  }

  const transportTiming = useMemo(
    () => (recording ? { ...timing, total: RECORDING_TOTAL } : timing),
    [recording, timing],
  )
  const transport = useTransport({
    timing: transportTiming,
    beatsPerBar: meter.beatsPerBar,
    bpm: initialBpm,
    initialBeat: 0,
    metronome: recording ? recording.withClick : metronome,
  })
  const running = transport.state.status === 'running'
  const bpm = transport.state.bpm

  const update = (change: (content: RevisionContent) => RevisionContent) => {
    setDraft((current) => change(current))
  }

  const changeMeter = (signature: string) =>
    update((current) => {
      const next: RevisionContent = { ...applyMetadata(current, { timeSignature: signature }) }
      delete next.beatUnit
      const rule = ruleOf(current)
      return rule ? { ...next, beatMap: estimateBeatMap(next, rule) } : next
    })

  const tap = () => {
    taps.current = [...taps.current, performance.now() / 1000].slice(-12)
    const value = tapTempo(taps.current)
    if (value !== null) transport.setBpm(value)
  }

  // Escuchar: el mapa que se está editando, con clic, desde una aparición o el principio.
  const preview = (from: number) => {
    transport.setLoop(null)
    transport.start({ from, countIn: countInBars * meter.beatsPerBar })
  }

  const startRecording = (from: number) => {
    const event = timing.events[from]
    if (!event) return
    setRecording({ from, withClick, startBeat: event.start, origin: null, marks: [] })
    if (withClick) {
      transport.setLoop(null)
      transport.start({ from: event.start, countIn: Math.max(1, countInBars) * meter.beatsPerBar })
    } else if (running) {
      transport.stop()
    }
  }

  const { beatOfEvent } = transport
  const mark = useCallback(
    (timeStamp: number) => {
      const current = recordingRef.current
      if (!current) return
      let relative: number | null
      if (current.withClick) {
        const beat = beatOfEvent(timeStamp)
        relative = beat === null ? null : beat - current.startBeat
      } else if (current.origin === null) {
        // Sin clic, el primer toque es el principio del primer acorde.
        setRecording({ ...current, origin: timeStamp })
        return
      } else {
        relative = ((timeStamp - current.origin) / 1000) * (bpm / 60)
      }
      const last = current.marks[current.marks.length - 1] ?? 0
      if (relative === null || relative < last + MIN_MARK_GAP) return
      setRecording({ ...current, marks: [...current.marks, relative] })
    },
    [beatOfEvent, bpm, setRecording],
  )

  const finishRecording = () => {
    if (running) transport.stop()
    if (recording && recording.marks.length > 0) {
      update((current) => ({
        ...current,
        beatMap: applyTake(current.beatMap, recording.from, recording.marks, grid),
      }))
    }
    setRecording(null)
  }

  const cancelRecording = () => {
    if (running) transport.stop()
    setRecording(null)
  }

  // Durante la grabación, la barra espaciadora, Intro y el pedal marcan; Escape cancela.
  const { pedal } = usePreferences()
  useEffect(() => {
    if (!recording) return
    const marking = new Set(['Space', 'Enter', ...pedal.next, ...pedal.playPause])
    const onKey = (event: KeyboardEvent) => {
      const key = event.key === ' ' ? 'Space' : event.key
      if (key === 'Escape') {
        event.preventDefault()
        if (running) transport.stop()
        setRecording(null)
        return
      }
      if (!marking.has(key) || event.repeat) return
      event.preventDefault()
      mark(event.timeStamp)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [recording, pedal, mark, running, transport, setRecording])

  const changeGrid = (next: Grid) => {
    setGrid(next)
    update((current) => ({ ...current, beatMap: requantize(current.beatMap, next) }))
  }

  const step = grid ?? 1

  async function save() {
    const issues = validateContent(draft)
    if (hasErrors(issues)) {
      setNotice(t.timing.saveError(issues.find((issue) => issue.severity === 'error')!.message))
      return
    }
    setSaving(true)
    try {
      const origin = hasTapped(draft.beatMap) ? 'tap' : (draft.beatMap.origin ?? 'manual')
      const content: RevisionContent = {
        ...draft,
        beatMap: { ...draft.beatMap, status: 'reviewed', origin },
        provenance: { ...draft.provenance, beatMap: { kind: 'user', method: origin } },
      }
      const result = await saveRevision(getDb(), arrangementId, content, {
        now: new Date().toISOString(),
        reason: t.timing.reason,
      })
      saveDetail({})
      setNotice(result.created ? t.timing.saved : t.timing.savedNothing)
    } catch (cause) {
      setNotice(t.timing.saveError(cause instanceof Error ? cause.message : String(cause)))
    } finally {
      setSaving(false)
    }
  }

  const back = (event: React.MouseEvent) => {
    if (dirty && !window.confirm(`${t.timing.unsaved}. ¿Salir sin guardar?`)) event.preventDefault()
  }

  const g = t.timing
  const status = startingPoint.beatMap.status === 'reviewed' && !completeBeatMap(base).fromRule
  const firstOf = (occurrenceId: string) =>
    timing.events.findIndex((event) => event.occurrence === occurrenceId)
  const totalBars = formatBeats(Math.round((timing.total / meter.beatsPerBar) * 10) / 10)
  const seconds = Math.round((timing.total * 60) / bpm)
  const duration = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`

  if (timing.events.length === 0) {
    return (
      <div className={common.page}>
        <p className={common.notice}>{g.noChords}</p>
        <a href={`#${paths.play(arrangementId)}`}>
          <button>← {g.back}</button>
        </a>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <a href={`#${paths.play(arrangementId)}`} onClick={back}>
          <button>← {g.back}</button>
        </a>
        <h1>
          {g.title} · {doc.song.title}
        </h1>
        <span className={status ? common.badge : common.badgeWarn}>
          {g.status[base.beatMap.status === 'absent' ? 'absent' : base.beatMap.status]}
        </span>
        {dirty && <span className={common.badgeWarn}>{g.unsaved}</span>}
        <button
          className={common.toggleOn}
          disabled={saving || running}
          onClick={() => void save()}
        >
          {saving ? g.saving : g.save}
        </button>
      </div>
      <p className={common.lead}>{g.lead}</p>
      {notice && (
        <p className={common.notice} role="status">
          {notice}
        </p>
      )}

      <div className={styles.tools}>
        <section>
          <h2>{g.meter}</h2>
          <div className={common.row}>
            {METERS.map((signature) => (
              <button
                key={signature}
                aria-pressed={meter.timeSignature === signature && !meter.assumed}
                className={
                  meter.timeSignature === signature && !meter.assumed ? common.toggleOn : undefined
                }
                onClick={() => changeMeter(signature)}
              >
                {signature}
              </button>
            ))}
          </div>
          <p className={`${common.muted} ${common.small}`}>
            {meter.assumed
              ? t.transport.meterAssumed(meter.timeSignature)
              : g.meterHint(meter.beatsPerBar)}
            {' · '}
            {g.total(totalBars, duration)}
          </p>
          <h2 style={{ marginTop: 12 }}>{g.estimate}</h2>
          <div className={common.row}>
            {ESTIMATE_RULES.map((rule) => (
              <button
                key={rule}
                onClick={() =>
                  update((current) => ({ ...current, beatMap: estimateBeatMap(current, rule) }))
                }
              >
                {g.rules[rule]}
              </button>
            ))}
          </div>
          <p className={`${common.muted} ${common.small}`}>{g.estimateHint}</p>
        </section>

        <section>
          <h2>{g.record}</h2>
          <p className={`${common.muted} ${common.small}`} style={{ marginTop: 0 }}>
            {g.recordHint}
          </p>
          <div className={common.row}>
            <span>{g.tempo}</span>
            <button
              aria-label={t.transport.slower}
              onClick={() => transport.setBpm(clampBpm(bpm - 1))}
            >
              −
            </button>
            <strong>{t.transport.bpm(bpm)}</strong>
            <button
              aria-label={t.transport.faster}
              onClick={() => transport.setBpm(clampBpm(bpm + 1))}
            >
              +
            </button>
            <button onPointerDown={tap} title={t.transport.tapHint}>
              {t.transport.tap}
            </button>
          </div>
          <div className={common.row} style={{ marginTop: 8 }}>
            {([true, false] as const).map((value) => (
              <button
                key={String(value)}
                aria-pressed={withClick === value}
                className={withClick === value ? common.toggleOn : undefined}
                onClick={() => setWithClick(value)}
              >
                {value ? g.withClick : g.withoutClick}
              </button>
            ))}
          </div>
          <div className={common.row} style={{ marginTop: 8 }}>
            <button className={common.toggleOn} onClick={() => startRecording(0)}>
              ● {g.recordFromStart}
            </button>
            {running && !recording ? (
              <button onClick={transport.stop}>■ {g.stopPreview}</button>
            ) : (
              <button onClick={() => preview(0)}>▶ {g.preview}</button>
            )}
          </div>
        </section>

        {hasTapped(draft.beatMap) && (
          <section>
            <h2>{g.grid}</h2>
            <div className={common.row}>
              {([1, 0.5, null] as const).map((value) => (
                <button
                  key={String(value)}
                  aria-pressed={grid === value}
                  className={grid === value ? common.toggleOn : undefined}
                  onClick={() => changeGrid(value)}
                >
                  {g.grids[value === null ? 'none' : value === 1 ? '1' : '0.5']}
                </button>
              ))}
            </div>
            <p className={`${common.muted} ${common.small}`}>{g.gridHint}</p>
          </section>
        )}
      </div>

      <h2 style={{ marginTop: 20 }}>{g.sequence}</h2>
      <p className={`${common.muted} ${common.small}`}>{g.sequenceHint}</p>
      {draft.sequence.map((occurrence, position) => {
        const section = draft.sections.find((item) => item.id === occurrence.section)
        if (!section) return null
        const events = timing.events.filter((event) => event.occurrence === occurrence.id)
        const check = bars.find((item) => item.occurrence === occurrence.id)
        const first = firstOf(occurrence.id)
        return (
          <section
            key={occurrence.id}
            className={styles.occurrence}
            data-occurrence={occurrence.id}
          >
            <div className={styles.occurrenceHead}>
              <h2>{sectionJumpLabel(section, show)}</h2>
              {check && check.remainder !== 0 && (
                <span className={styles.warn}>
                  {g.offBar(
                    check.remainder > meter.beatsPerBar / 2
                      ? -(meter.beatsPerBar - check.remainder)
                      : check.remainder,
                  )}
                </span>
              )}
              {first >= 0 && (
                <>
                  <button onClick={() => preview(timing.events[first]!.start)}>▶</button>
                  <button onClick={() => startRecording(first)}>● {g.recordHere}</button>
                </>
              )}
              <button onClick={() => update((current) => repeatOccurrence(current, occurrence.id))}>
                {g.repeat}
              </button>
              <button
                aria-label={g.moveUp}
                disabled={position === 0}
                onClick={() => update((current) => moveOccurrence(current, occurrence.id, -1))}
              >
                ↑
              </button>
              <button
                aria-label={g.moveDown}
                disabled={position === draft.sequence.length - 1}
                onClick={() => update((current) => moveOccurrence(current, occurrence.id, 1))}
              >
                ↓
              </button>
              <button
                disabled={!canRemoveOccurrence(draft, occurrence.id)}
                onClick={() => update((current) => removeOccurrence(current, occurrence.id))}
              >
                {g.remove}
              </button>
            </div>
            <div className={styles.events}>
              {events.map((event) => {
                const at = barAndBeat(event.start, meter.beatsPerBar)
                const tapped = draft.beatMap.events[event.index]?.tapped
                return (
                  <div
                    key={event.index}
                    className={
                      running && !recording && transport.index === event.index
                        ? styles.eventCurrent
                        : styles.event
                    }
                  >
                    <span className={styles.chord}>{symbolAt(event.index)}</span>
                    <span className={styles.start}>{g.barLabel(at.bar, at.beat)}</span>
                    <button
                      aria-label={g.shorter}
                      disabled={event.beats <= step}
                      onClick={() =>
                        update((current) => ({
                          ...current,
                          beatMap: setEventBeats(current.beatMap, event.index, event.beats - step),
                        }))
                      }
                    >
                      −
                    </button>
                    <span className={styles.beats}>{formatBeats(event.beats)}</span>
                    <button
                      aria-label={g.longer}
                      onClick={() =>
                        update((current) => ({
                          ...current,
                          beatMap: setEventBeats(current.beatMap, event.index, event.beats + step),
                        }))
                      }
                    >
                      +
                    </button>
                    {tapped !== undefined && (
                      <span className={styles.tapped} title={g.tapped}>
                        ●
                      </span>
                    )}
                  </div>
                )
              })}
            </div>
          </section>
        )
      })}

      {recording && (
        <div className={styles.recorder} role="dialog" aria-label={g.recording}>
          <div className={styles.recorderHead}>
            <strong>
              ● {g.recording} · {t.transport.bpm(bpm)} ·{' '}
              {recording.withClick ? g.withClick : g.withoutClick}
            </strong>
            <span className={common.muted}>{g.marks(recording.marks.length)}</span>
          </div>
          <button
            className={styles.pad}
            onPointerDown={(event) => {
              event.preventDefault()
              mark(event.timeStamp)
            }}
          >
            {transport.phase === 'countIn' && transport.countInLeft > 0 ? (
              <span className={styles.countIn}>{transport.countInLeft}</span>
            ) : !recording.withClick && recording.origin === null ? (
              <span>{g.waitingFirst}</span>
            ) : (
              <>
                <span className={styles.padNext}>{g.nowPlaying}</span>
                <span className={styles.padChord}>
                  {symbolAt(recording.from + recording.marks.length)}
                </span>
                <span className={styles.padNext}>
                  {g.nextChange}: {symbolAt(recording.from + recording.marks.length + 1)}
                </span>
              </>
            )}
            <span className={styles.padNext}>{g.tapPad}</span>
          </button>
          <div className={styles.recorderActions}>
            <button
              disabled={recording.marks.length === 0}
              onClick={() => setRecording({ ...recording, marks: recording.marks.slice(0, -1) })}
            >
              {g.undoMark}
            </button>
            <button className={common.toggleOn} onClick={finishRecording}>
              {g.finish}
            </button>
            <button onClick={cancelRecording}>{g.cancel}</button>
          </div>
        </div>
      )}
    </div>
  )
}
